"use client";

import { useEffect, useRef, useState } from "react";
import { useValidation } from "@/hooks";
import { authSchema } from "@/validations";
import { AuthForm, AuthTouched } from "@/types";
import { useSearchParams } from "next/navigation";

//NextAuth
import { signIn, signOut } from "next-auth/react";
import clearAllClientState from "@/utils/clearAllClientState";
import { toast } from "react-toastify";

const useAuth = () => {
  // const router = useRouter();
  const searchParams = useSearchParams();
  const error = searchParams.get("error");
  const hasShownError = useRef(false);
  //pega a linguagem do localStorage ou o padrão
  //const pathname = usePathname();
  // const storageLang = localStorage.getItem('lang') || 'pt-BR';
  // const currentLang = pathname.split('/')[1] || storageLang;

  const form: AuthForm = {
    email: "",
    password: "",
  };

  const [authForm, setAuthForm] = useState<AuthForm>(form);
  const [touched, setTouched] = useState<AuthTouched>({});

  const { errors, validateField, validateForm } = useValidation(authSchema);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setAuthForm({
      ...authForm,
      [name]: value,
    });
    validateField(name, value);
  };

  const handleBlur = (e: any) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    validateField(name, authForm[name as keyof AuthForm]);
  };

  const [isLoading, setIsLoading] = useState(false);

  /*
   'evertonsan2019@gmail.com',
    'bola8barbearia@gmail.com',
    'trevisanbarbearia@gmail.com',
    'vigscorts@gmail.com',
    'thebarbershopluiz2019@gmail.com',
    'teste@xgain.com.br',
    'hugovictor5504@gmail.com',
    'barbeariadiegobarbafina@gmail.com',
  */

  const AUTHORIZED_USERS = ["gabriel.lucas96@icloud.com", "teste@xgain.com.br"];

  //Validação de login por email
  /*

  const handleLogin = async (e: any) => {
    e.preventDefault();

    const isValid = await validateForm(authForm);
    if (!isValid) {
      setTouched({
        email: true,
        password: true,
      });
      return;
    }

    const email = authForm.email.trim().toLowerCase();

    if (!AUTHORIZED_USERS.includes(email)) {
      // Redireciona para sistema externo se o email não for autorizado
      window.location.href = 'https://app.xgain.com.br/sistema/';
      return;
    }

    setIsLoading(true);

    const res = await signIn('credentials', {
      email: authForm.email,
      password: authForm.password,
      // redirect: false, // use se quiser tratar manualmente
    });
  };
  
  */

  const handleLogin = async (e: any) => {
    e.preventDefault();

    const isValid = await validateForm(authForm);
    if (!isValid) {
      setTouched({
        email: true,
        password: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Obter URL de destino dos query params ou usar dashboard como padrão
      const searchParams = new URLSearchParams(window.location.search);
      const callbackUrl = searchParams.get("callbackUrl");

      // Se não houver callbackUrl, mandar o usuário para a agenda
      // localizada. A URL atual do login contém o idioma no primeiro segmento
      // (ex: "/pt-BR/login"), então a extraímos para montar o destino.
      let finalCallbackUrl: string | undefined;
      if (callbackUrl) {
        finalCallbackUrl = callbackUrl;
      } else {
        const currentPath = window.location.pathname;
        const parts = currentPath.split("/").filter(Boolean);
        const langSegment = parts[0] ?? "pt-BR";
        finalCallbackUrl = `/${langSegment}/schedule`;
      }

      const result = await signIn("credentials", {
        email: authForm.email,
        password: authForm.password,
        redirect: true,
        ...(finalCallbackUrl && { callbackUrl: finalCallbackUrl }),
      });

      // Se redirect for false, tratar manualmente
      if (result?.error) {
        setIsLoading(false);
        if (result.error === "CredentialsSignin") {
          toast.error("Usuário ou senha inválidos");
        } else {
          toast.error("Erro ao fazer login. Tente novamente.");
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Erro no login:", error);
      toast.error("Erro inesperado ao fazer login. Tente novamente.");
    }
  };

  const handleLogout = async () => {
    try {
      // Best effort clear of local/session storage and non-HttpOnly cookies
      clearAllClientState();
    } catch (_) {}
    // Trigger NextAuth signOut to clear server/session cookies and redirect
    await signOut({ callbackUrl: "/" });
  };

  useEffect(() => {
    if (!hasShownError.current && error) {
      hasShownError.current = true;

      let errorMessage = "Erro ao fazer login";
      switch (error) {
        case "CredentialsSignin":
          errorMessage = "Usuário ou senha inválidos";
          break;
        case "Configuration":
          errorMessage = "Erro de configuração do servidor";
          break;
        case "AccessDenied":
          errorMessage = "Acesso negado";
          break;
        case "Verification":
          errorMessage = "Erro na verificação do token";
          break;
        default:
          errorMessage = `Erro ao fazer login: ${error}`;
      }

      toast.error(errorMessage);

      // Remover parâmetro de erro da URL sem recarregar a página
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [error]);

  return {
    authForm,
    handleInputChange,
    handleLogin,
    handleBlur,
    errors,
    touched,
    handleLogout,
    isLoading,
  };
};

export default useAuth;
